import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import AdminNavbar from '../../components/AdminNavbar';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import { 
    fetchSingleCatalog, fetchAdminProducts, 
    updateProductPositions 
} from '../../store/slices/adminSlice';
import { getCurrencySymbol } from '../../helper/currencyHelper';
import { getProductCoverUrl } from '../../helper/imageHelper';

const STOCK_STATUSES = [
    { value: 'available', label: 'Disponible', color: 'success' },
    { value: 'ondemand', label: 'Bajo Pedido', color: 'info' },
    { value: 'ask', label: 'Consultar', color: 'warning' },
    { value: 'transit', label: 'En Tránsito', color: 'primary' },
    { value: 'soldout', label: 'Agotado', color: 'danger' }
];

const ProductListView = () => {
    const { catalogID } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { 
        authUser, vendorProfile, catalogs, products, productsLoading, error 
    } = useSelector((state) => state.admin);

    const catalog = catalogs.find(c => c.id === catalogID);

    // Filters state
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [discountFilter, setDiscountFilter] = useState('all'); // 'all', 'discounted', 'nodiscount'
    const [stockFilter, setStockFilter] = useState('all');

    // Drag and Drop state
    const [draggedIdx, setDraggedIdx] = useState(null);

    useEffect(() => {
        if (authUser?.uid && catalogID) {
            if (!catalog) {
                dispatch(fetchSingleCatalog({ userID: authUser.uid, catalogID }));
            }
            dispatch(fetchAdminProducts({ userID: authUser.uid, catalogID }));
        }
    }, [dispatch, authUser, catalogID, catalog]);

    const isPremiumV2 = vendorProfile?.subscription === 'premiumv2';
    const productLimitReached = !isPremiumV2 && products.length >= 25;
    const currencySymbol = catalog ? getCurrencySymbol(catalog) : '$';

    // Local In-Memory Filtering (Matches Android filtering logic)
    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            // 1. Text Search (name or description)
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const nameMatch = (product.name || '').toLowerCase().includes(term);
                const descMatch = (product.description || '').toLowerCase().includes(term);
                if (!nameMatch && !descMatch) return false;
            }

            // 2. Price Range
            const price = parseFloat(product.price) || 0;
            if (minPrice !== '' && !isNaN(parseFloat(minPrice))) {
                if (price < parseFloat(minPrice)) return false;
            }
            if (maxPrice !== '' && !isNaN(parseFloat(maxPrice))) {
                if (price > parseFloat(maxPrice)) return false;
            }

            // 3. Discount Filter
            const discount = parseFloat(product.discount) || 0;
            if (discountFilter === 'discounted' && discount <= 0) return false;
            if (discountFilter === 'nodiscount' && discount > 0) return false;

            // 4. Stock Filter
            if (stockFilter !== 'all' && product.stock !== stockFilter) return false;

            return true;
        });
    }, [products, searchTerm, minPrice, maxPrice, discountFilter, stockFilter]);

    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (searchTerm.trim()) count++;
        if (minPrice !== '') count++;
        if (maxPrice !== '') count++;
        if (discountFilter !== 'all') count++;
        if (stockFilter !== 'all') count++;
        return count;
    }, [searchTerm, minPrice, maxPrice, discountFilter, stockFilter]);

    const clearAllFilters = () => {
        setSearchTerm('');
        setMinPrice('');
        setMaxPrice('');
        setDiscountFilter('all');
        setStockFilter('all');
    };

    // Drag and Drop reordering
    const handleDragStart = (idx) => {
        setDraggedIdx(idx);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (dropIdx) => {
        if (draggedIdx === null || draggedIdx === dropIdx) return;
        const reordered = [...products];
        const [moved] = reordered.splice(draggedIdx, 1);
        reordered.splice(dropIdx, 0, moved);
        setDraggedIdx(null);
        await dispatch(updateProductPositions({
            userID: authUser.uid,
            catalogID,
            reorderedProducts: reordered
        }));
    };

    return (
        <div className="bg-gray-50 min-vh-100 font-sans pb-60">
            <AdminNavbar />
            <AdminBreadcrumb catalog={catalog} />

            <div className="container container-lg">
                {/* Catalog Header Bar */}
                <div className="bg-white p-16 p-md-20 rounded-24 border shadow-2xs mb-20">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-12 gap-md-16">
                        <div className="d-flex align-items-center gap-12">
                            <span 
                                className="w-16 h-16 rounded-circle flex-shrink-0"
                                style={{ backgroundColor: catalog?.color || '#607D8B' }}
                            ></span>
                            <div>
                                <h4 className="fw-bold text-gray-900 mb-2 text-md text-md-xl">{catalog?.name || 'Cargando catálogo...'}</h4>
                                <p className="text-gray-500 text-2xs text-md-xs mb-0">
                                    Moneda: <strong className="text-gray-900">{currencySymbol}</strong> • {products.length} producto(s)
                                </p>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-8 flex-wrap w-100 w-sm-auto justify-content-end">
                            <button 
                                type="button"
                                className={`btn btn-sm rounded-pill px-12 px-md-16 py-8 text-xs fw-semibold flex-align gap-6 transition-2 ${
                                    activeFiltersCount > 0 ? 'btn-main' : 'btn-outline-gray-200 text-gray-700'
                                }`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <i className="ph ph-funnel text-sm"></i>
                                Filtros {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                            </button>

                            <a 
                                href={`/c/${authUser?.uid}/${catalogID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-gray-200 btn-sm rounded-pill px-12 px-md-14 py-8 text-xs fw-semibold text-gray-700 flex-align gap-6"
                            >
                                <i className="ph ph-arrow-square-out text-sm"></i>
                                <span className="d-none d-sm-inline">Tienda Pública</span>
                            </a>

                            <button 
                                type="button"
                                className="btn btn-main btn-sm rounded-pill px-14 px-md-18 py-8 fw-bold text-xs flex-align gap-6 shadow-xs ms-auto ms-sm-0"
                                onClick={() => navigate(`/admin/dashboard/${catalogID}/new`)}
                                disabled={productLimitReached}
                            >
                                <i className="ph ph-plus text-sm"></i>
                                Nuevo Producto
                            </button>
                        </div>
                    </div>

                    {/* Filter Panel (Collapsible) */}
                    {showFilters && (
                        <div className="mt-16 mt-md-20 pt-16 pt-md-20 border-top bg-gray-50 p-12 p-md-16 rounded-16 border">
                            <div className="row g-12 align-items-end">
                                {/* Text Search */}
                                <div className="col-12 col-md-4">
                                    <label className="form-label text-2xs fw-bold text-gray-700 text-uppercase mb-4">Buscar por Nombre/Descripción</label>
                                    <div className="position-relative">
                                        <input 
                                            type="text"
                                            className="form-control form-control-sm rounded-12 ps-32 text-xs"
                                            placeholder="Buscar producto..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <i className="ph ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-10 text-gray-400 text-xs"></i>
                                    </div>
                                </div>

                                {/* Price Min */}
                                <div className="col-6 col-md-2">
                                    <label className="form-label text-2xs fw-bold text-gray-700 text-uppercase mb-4">Precio Mín ({currencySymbol})</label>
                                    <input 
                                        type="number"
                                        step="any"
                                        className="form-control form-control-sm rounded-12 text-xs"
                                        placeholder="Min"
                                        value={minPrice}
                                        onChange={(e) => setMinPrice(e.target.value)}
                                    />
                                </div>

                                {/* Price Max */}
                                <div className="col-6 col-md-2">
                                    <label className="form-label text-2xs fw-bold text-gray-700 text-uppercase mb-4">Precio Máx ({currencySymbol})</label>
                                    <input 
                                        type="number"
                                        step="any"
                                        className="form-control form-control-sm rounded-12 text-xs"
                                        placeholder="Max"
                                        value={maxPrice}
                                        onChange={(e) => setMaxPrice(e.target.value)}
                                    />
                                </div>

                                {/* Discount Toggle */}
                                <div className="col-6 col-md-2">
                                    <label className="form-label text-2xs fw-bold text-gray-700 text-uppercase mb-4">Descuento</label>
                                    <select 
                                        className="form-select form-select-sm rounded-12 text-xs"
                                        value={discountFilter}
                                        onChange={(e) => setDiscountFilter(e.target.value)}
                                    >
                                        <option value="all">Todos</option>
                                        <option value="discounted">Con Descuento</option>
                                        <option value="nodiscount">Sin Descuento</option>
                                    </select>
                                </div>

                                {/* Stock Status */}
                                <div className="col-6 col-md-2">
                                    <label className="form-label text-2xs fw-bold text-gray-700 text-uppercase mb-4">Estado Stock</label>
                                    <select 
                                        className="form-select form-select-sm rounded-12 text-xs"
                                        value={stockFilter}
                                        onChange={(e) => setStockFilter(e.target.value)}
                                    >
                                        <option value="all">Cualquiera</option>
                                        {STOCK_STATUSES.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Active Filter Chips */}
                    {activeFiltersCount > 0 && (
                        <div className="d-flex align-items-center gap-8 flex-wrap mt-12 pt-12 border-top">
                            <span className="text-2xs text-gray-400 fw-semibold me-4">Filtros activos:</span>
                            
                            {searchTerm.trim() && (
                                <span className="badge bg-main-100 text-main-700 rounded-pill text-3xs px-10 py-4 flex-align gap-4">
                                    Texto: "{searchTerm}"
                                    <i className="ph ph-x cursor-pointer ms-4" onClick={() => setSearchTerm('')}></i>
                                </span>
                            )}

                            {(minPrice !== '' || maxPrice !== '') && (
                                <span className="badge bg-main-100 text-main-700 rounded-pill text-3xs px-10 py-4 flex-align gap-4">
                                    Precio: {minPrice || 0} - {maxPrice || '∞'} {currencySymbol}
                                    <i className="ph ph-x cursor-pointer ms-4" onClick={() => { setMinPrice(''); setMaxPrice(''); }}></i>
                                </span>
                            )}

                            {discountFilter !== 'all' && (
                                <span className="badge bg-main-100 text-main-700 rounded-pill text-3xs px-10 py-4 flex-align gap-4">
                                    Descuento: {discountFilter === 'discounted' ? 'Sí' : 'No'}
                                    <i className="ph ph-x cursor-pointer ms-4" onClick={() => setDiscountFilter('all')}></i>
                                </span>
                            )}

                            {stockFilter !== 'all' && (
                                <span className="badge bg-main-100 text-main-700 rounded-pill text-3xs px-10 py-4 flex-align gap-4">
                                    Stock: {STOCK_STATUSES.find(s => s.value === stockFilter)?.label}
                                    <i className="ph ph-x cursor-pointer ms-4" onClick={() => setStockFilter('all')}></i>
                                </span>
                            )}

                            <button 
                                type="button" 
                                className="btn btn-link text-danger text-3xs p-0 ms-8 text-decoration-none fw-semibold"
                                onClick={clearAllFilters}
                            >
                                Limpiar Filtros
                            </button>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="alert alert-danger py-12 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                        <i className="ph ph-warning-circle text-md"></i>
                        <span>{error}</span>
                    </div>
                )}

                {/* Reorder Notice */}
                {products.length > 1 && !activeFiltersCount && (
                    <div className="alert alert-info py-8 px-14 text-2xs rounded-12 mb-16 flex-align gap-8">
                        <i className="ph ph-arrows-down-up text-sm"></i>
                        <span>Arrastra las filas desde el icono <i className="ph ph-dots-six-vertical"></i> para cambiar el orden de tus productos.</span>
                    </div>
                )}

                {/* Products List & Table View */}
                <div>
                    {productsLoading ? (
                        <div className="text-center py-60 bg-white rounded-24 border shadow-2xs">
                            <div className="spinner-border text-main-600 mb-12" role="status" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                            <p className="text-gray-500 text-xs mb-0">Cargando productos...</p>
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="text-center py-60 bg-white rounded-24 border shadow-2xs">
                            <i className="ph ph-package text-4xl text-gray-300 mb-12 d-block"></i>
                            <h6 className="fw-bold text-gray-700 mb-4">
                                {activeFiltersCount > 0 ? 'No hay productos que coincidan con los filtros' : 'Este catálogo aún no tiene productos'}
                            </h6>
                            <p className="text-gray-400 text-xs mb-16">
                                {activeFiltersCount > 0 ? 'Intenta modificar o limpiar los filtros activos.' : 'Agrega tu primer producto para comenzar.'}
                            </p>
                            {activeFiltersCount > 0 ? (
                                <button 
                                    type="button" 
                                    className="btn btn-outline-main btn-sm rounded-pill px-20 py-8 text-xs fw-semibold"
                                    onClick={clearAllFilters}
                                >
                                    Limpiar Filtros
                                </button>
                            ) : (
                                <button 
                                    type="button" 
                                    className="btn btn-main btn-sm rounded-pill px-20 py-8 text-xs fw-bold"
                                    onClick={() => navigate(`/admin/dashboard/${catalogID}/new`)}
                                >
                                    Agregar Primer Producto
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* MOBILE CARD VIEW (d-md-none): 3-section card list */}
                            <div className="d-flex flex-column gap-12 w-100 d-md-none">
                                {filteredProducts.map((product, idx) => {
                                    const stockObj = STOCK_STATUSES.find(s => s.value === product.stock) || STOCK_STATUSES[0];
                                    const coverUrl = getProductCoverUrl(product);

                                    const originalPrice = parseFloat(product.price) || 0;
                                    const discountPct = parseFloat(product.discount) || 0;
                                    const finalPrice = discountPct > 0 ? (originalPrice * (1 - discountPct / 100)).toFixed(2) : originalPrice.toFixed(2);

                                    return (
                                        <div 
                                            key={product.id}
                                            draggable={!activeFiltersCount}
                                            onDragStart={() => handleDragStart(idx)}
                                            onDragOver={handleDragOver}
                                            onDrop={() => handleDrop(idx)}
                                            className={`bg-white rounded-16 border p-12 shadow-2xs hover-shadow-md transition-2 cursor-pointer w-100 overflow-hidden ${
                                                draggedIdx === idx ? 'opacity-50 bg-gray-100' : ''
                                            }`}
                                            onClick={() => navigate(`/admin/dashboard/${catalogID}/${product.id}`)}
                                        >
                                            <div className="d-flex align-items-center gap-12 w-100 overflow-hidden">
                                                {/* Drag Handle */}
                                                <div 
                                                    className="text-gray-400 cursor-grab flex-shrink-0" 
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="Arrastrar para reordenar"
                                                >
                                                    <i className="ph ph-dots-six-vertical text-md"></i>
                                                </div>

                                                {/* Section 1: Image */}
                                                <img 
                                                    src={coverUrl} 
                                                    alt={product.name} 
                                                    className="w-56 h-56 rounded-12 object-fit-cover border shadow-2xs flex-shrink-0 bg-white"
                                                />

                                                {/* Section 2: Title - Description - Stock */}
                                                <div className="flex-grow-1 overflow-hidden min-w-0 text-start">
                                                    <span className="fw-bold text-gray-900 text-xs d-block text-truncate mb-2">
                                                        {product.name}
                                                    </span>
                                                    <p 
                                                        className="text-2xs text-gray-500 mb-6 text-start"
                                                        style={{ 
                                                            display: '-webkit-box', 
                                                            WebkitLineClamp: 2, 
                                                            WebkitBoxOrient: 'vertical', 
                                                            overflow: 'hidden', 
                                                            lineHeight: '1.3',
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        {product.description || 'Sin descripción'}
                                                    </p>
                                                    <span className={`badge bg-${stockObj.color}-100 text-${stockObj.color}-700 rounded-pill text-3xs px-8 py-2 d-inline-block`}>
                                                        {stockObj.label}
                                                    </span>
                                                </div>

                                                {/* Section 3: Undiscounted price - Price - Discount percentage */}
                                                <div className="flex-shrink-0 text-end ps-8" style={{ minWidth: '80px' }}>
                                                    {discountPct > 0 && (
                                                        <span className="text-3xs text-gray-400 text-decoration-line-through d-block mb-2">
                                                            {currencySymbol}{originalPrice.toFixed(2)}
                                                        </span>
                                                    )}
                                                    <span className="fw-bold text-gray-900 text-xs d-block mb-2">
                                                        {currencySymbol}{finalPrice}
                                                    </span>
                                                    {discountPct > 0 && (
                                                        <span className="badge bg-danger-100 text-danger-700 text-3xs rounded-pill px-6 py-2 fw-bold d-inline-block">
                                                            -{discountPct}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* DESKTOP TABLE VIEW (d-none d-md-block): Full featured table approach */}
                            <div className="bg-white rounded-24 border shadow-2xs overflow-hidden d-none d-md-block">
                                <table className="table table-hover align-middle mb-0 text-sm">
                                    <thead className="bg-gray-50 text-gray-500 text-2xs text-uppercase">
                                        <tr>
                                            <th style={{ width: '36px' }} className="px-12 text-center"></th>
                                            <th className="py-12 px-16 text-start">Producto</th>
                                            <th className="py-12 px-16 text-center">Estado Stock</th>
                                            <th className="py-12 px-16 text-end">Precio Regular</th>
                                            <th className="py-12 px-16 text-end">Precio Final</th>
                                            <th className="py-12 px-16 text-center">Descuento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProducts.map((product, idx) => {
                                            const stockObj = STOCK_STATUSES.find(s => s.value === product.stock) || STOCK_STATUSES[0];
                                            const coverUrl = getProductCoverUrl(product);

                                            const originalPrice = parseFloat(product.price) || 0;
                                            const discountPct = parseFloat(product.discount) || 0;
                                            const finalPrice = discountPct > 0 ? (originalPrice * (1 - discountPct / 100)).toFixed(2) : originalPrice.toFixed(2);

                                            return (
                                                <tr 
                                                    key={product.id}
                                                    draggable={!activeFiltersCount}
                                                    onDragStart={() => handleDragStart(idx)}
                                                    onDragOver={handleDragOver}
                                                    onDrop={() => handleDrop(idx)}
                                                    className={`cursor-pointer ${draggedIdx === idx ? 'opacity-50 bg-gray-100' : ''}`}
                                                    onClick={() => navigate(`/admin/dashboard/${catalogID}/${product.id}`)}
                                                >
                                                    <td className="text-gray-400 cursor-grab px-12 text-center" onClick={(e) => e.stopPropagation()}>
                                                        <i className="ph ph-dots-six-vertical text-md"></i>
                                                    </td>

                                                    <td className="py-14 px-16 text-start" style={{ maxWidth: '320px' }}>
                                                        <div className="d-flex align-items-center gap-12 text-start overflow-hidden">
                                                            <img 
                                                                src={coverUrl} 
                                                                alt={product.name} 
                                                                className="w-48 h-48 rounded-12 object-fit-cover border shadow-2xs flex-shrink-0 bg-white"
                                                            />
                                                            <div className="text-start flex-grow-1 overflow-hidden">
                                                                <span className="fw-bold text-gray-900 text-xs text-sm-sm d-block text-start text-truncate mb-2">{product.name}</span>
                                                                <span className="text-2xs text-gray-500 d-block text-start text-truncate" style={{ maxWidth: '240px' }}>
                                                                    {product.description || 'Sin descripción'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className="py-14 px-16 text-center">
                                                        <span className={`badge bg-${stockObj.color}-100 text-${stockObj.color}-700 rounded-pill text-3xs px-10 py-4 fw-bold`}>
                                                            {stockObj.label}
                                                        </span>
                                                    </td>

                                                    {/* Regular Pre-discount Price */}
                                                    <td className="py-14 px-16 text-end align-middle">
                                                        <span className={`text-xs ${discountPct > 0 ? 'text-gray-400 text-decoration-line-through' : 'fw-bold text-gray-900'}`}>
                                                            {currencySymbol}{originalPrice.toFixed(2)}
                                                        </span>
                                                    </td>

                                                    {/* Discounted Final Price */}
                                                    <td className="py-14 px-16 text-end align-middle">
                                                        <span className="fw-bold text-gray-900 text-xs text-sm-sm">
                                                            {currencySymbol}{finalPrice}
                                                        </span>
                                                    </td>

                                                    {/* Discount Percentage */}
                                                    <td className="py-14 px-16 text-center align-middle">
                                                        {discountPct > 0 ? (
                                                            <span className="badge bg-danger-100 text-danger-700 text-3xs rounded-pill px-8 py-4 fw-bold">
                                                                -{discountPct}%
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-300 text-xs">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductListView;
